/**
 * Pre-built form field components.
 *
 * Each field wraps a shadcn/ui control and is registered in index.ts via
 * createFormHook, making them available as:
 *
 *   <form.AppField name="email">
 *     {(field) => <field.InputField label="Email" type="email" />}
 *   </form.AppField>
 *
 * All fields use FieldWrapper which handles layout (stacked/inline/compact),
 * field masks (hidden/disabled), and label/description/error rendering.
 *
 * To add a new field type: create the component here, then register it in
 * the fieldComponents object in index.ts.
 */

import { type ReactNode, useId } from 'react'

import { CurrencyInput } from '@/frontend/components/currency-input'
import { DateInput, type DateInputProps } from '@/frontend/components/date-input'
import { DatePicker, type DatePickerProps } from '@/frontend/components/date-picker'
import { DateTimePicker, type DateTimePickerProps } from '@/frontend/components/datetime-picker'
import { useFieldContext, useFormContext } from '@/frontend/components/form/form-context'
import {
  type FieldMasks,
  type FormLayout,
  getFieldMask,
  useFormLayout,
} from '@/frontend/components/form/layouts/form-layout-context'
import { FormDescription, FormError, FormLabel } from '@/frontend/components/form/primitives'
import { NumericInput } from '@/frontend/components/numeric-input'
import { TimePicker, type TimePickerProps } from '@/frontend/components/time-picker'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { CheckboxGroup, type CheckboxGroupOption } from '@/frontend/components/ui/checkbox-group'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/frontend/components/ui/combobox'
import { Input } from '@/frontend/components/ui/input'
import { PhoneInput } from '@/frontend/components/ui/phone-input'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import { RichTextEditor, type RichTextEditorProps } from '@/frontend/components/ui/rich-text-editor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { Textarea } from '@/frontend/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/frontend/components/ui/toggle-group'
import { cn } from '@/frontend/lib/utils'

import { getFormLayout } from './layouts'

// ─── Shared types & wrapper ─────────────────────────────────────────

export interface BaseFieldProps {
  label?: string
  /** Show a required indicator (*) next to the label. */
  required?: boolean
  description?: ReactNode
  layout?: FormLayout
  className?: string
  layoutClassName?: string
}

/** Narrowed version of TanStack Form's FieldApi — only the shape FieldWrapper needs. */
interface FieldCtx {
  name: string
  state: { meta: { errors: Array<string | { message: string } | undefined> } }
}

interface FormFieldProps extends Omit<BaseFieldProps, 'className'> {
  errors?: Array<string | { message: string } | undefined>
  /** Render the control element. */
  render: (ctx: { id: string; disabled: boolean }) => ReactNode
  /** Use `id` instead of `htmlFor` on the label (e.g. for checkbox/radio) */
  labelUsesId?: boolean
  /** Reverse the layout order (control before label, e.g. checkboxes) */
  reverseLayout?: boolean
  disabled?: boolean
}

export function FormField({
  errors = [],
  label,
  required,
  description,
  layoutClassName,
  layout,
  render,
  labelUsesId,
  reverseLayout,
  disabled = false,
}: FormFieldProps) {
  const id = useId()
  const resolvedLayout = useFormLayout(layout)
  const hasErrors = errors.length > 0

  const Layout = getFormLayout(resolvedLayout)

  return (
    <Layout
      className={layoutClassName}
      reverseLayout={reverseLayout}
      label={
        label && (
          <FormLabel
            {...(labelUsesId ? { id } : { htmlFor: id })}
            required={required}
            disabled={disabled}
            error={hasErrors}
          >
            {label}
          </FormLabel>
        )
      }
      description={description && <FormDescription>{description}</FormDescription>}
      control={render({ id, disabled })}
      error={<FormError errors={errors} />}
    />
  )
}

interface FieldWrapperProps extends Omit<FormFieldProps, 'errors'> {
  field: FieldCtx
  masks?: FieldMasks
}

export function FieldWrapper({ field, masks, disabled, ...props }: FieldWrapperProps) {
  const mask = getFieldMask(field.name, masks)
  if (mask === 'hidden') return null

  return (
    <FormField
      {...props}
      errors={field.state.meta.errors}
      disabled={disabled || mask === 'disabled'}
    />
  )
}

/**
 * Internal hook for registry fields — extracts field and masks from context
 * so they can be passed to FieldWrapper as explicit props.
 */
function useFieldWrapperContext() {
  // biome-ignore lint/suspicious/noExplicitAny: registry fields access value/handlers generically
  const field = useFieldContext<any>()
  const form = useFormContext()
  const masks = (form.options as { masks?: FieldMasks }).masks
  return { field, masks }
}

// ─── Input ───────────────────────────────────────────────────────────

interface InputFieldProps extends BaseFieldProps, React.ComponentProps<'input'> {}

export function InputField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: InputFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <Input
          id={id}
          type={props.type ?? 'text'}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── PhoneInput ──────────────────────────────────────────────────────

interface PhoneInputFieldProps extends BaseFieldProps {
  placeholder?: string
  autoComplete?: string
  disabled?: boolean
  /** ISO 3166-1 alpha-2 country code the library falls back to for local-format input. */
  defaultCountry?: 'GB' | 'US'
}

export function PhoneInputField({
  label,
  required,
  description,
  layoutClassName,
  layout,
  defaultCountry,
  ...props
}: PhoneInputFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <PhoneInput
          id={id}
          defaultCountry={defaultCountry}
          value={field.state.value || undefined}
          onChange={(value: string | undefined) => field.handleChange(value ?? '')}
          onBlur={field.handleBlur}
          disabled={disabled}
          error={field.state.meta.errors.length > 0}
          aria-invalid={field.state.meta.errors.length > 0}
          {...props}
        />
      )}
    />
  )
}

// ─── Rich Text ───────────────────────────────────────────────────────

interface RichTextFieldProps
  extends BaseFieldProps,
    Pick<RichTextEditorProps, 'placeholder' | 'disabled'> {}

export function RichTextField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: RichTextFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ disabled }) => (
        <RichTextEditor
          value={field.state.value || undefined}
          onChange={(html) => field.handleChange(html)}
          disabled={disabled}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── Textarea ────────────────────────────────────────────────────────

interface TextareaFieldProps extends BaseFieldProps, React.ComponentProps<'textarea'> {}

export function TextareaField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: TextareaFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <Textarea
          id={id}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── NumericInput ────────────────────────────────────────────────────

interface NumericInputFieldProps
  extends BaseFieldProps,
    Omit<React.ComponentProps<typeof NumericInput>, 'value' | 'onChange'> {}

export function NumericInputField({
  emptyValue,
  scale,
  label,
  required,
  description,
  placeholder,
  className,
  layoutClassName,
  layout,
  ...props
}: NumericInputFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <NumericInput
          id={id}
          emptyValue={emptyValue}
          scale={scale}
          value={field.state.value}
          onChange={(value) => field.handleChange(value)}
          onBlur={field.handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
        />
      )}
    />
  )
}

// ─── CurrencyInput ───────────────────────────────────────────────────

interface CurrencyFieldProps
  extends BaseFieldProps,
    Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  /** Currency symbol shown as a prefix. Defaults to "£". */
  symbol?: string
}

export function CurrencyField({
  label,
  required,
  description,
  placeholder,
  symbol,
  className,
  layoutClassName,
  layout,
  ...props
}: CurrencyFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <CurrencyInput
          id={id}
          value={field.state.value ?? null}
          onChange={(value) => field.handleChange(value)}
          onBlur={field.handleBlur}
          placeholder={placeholder}
          symbol={symbol}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
        />
      )}
    />
  )
}

// ─── Select ──────────────────────────────────────────────────────────

interface SelectFieldProps<V>
  extends BaseFieldProps,
    Omit<React.ComponentProps<typeof Select<V>>, 'children'> {
  placeholder?: string
}

export function SelectField<V>({
  label,
  required,
  description,
  placeholder,
  className,
  layoutClassName,
  layout,
  items,
  onValueChange,
  ...selectProps
}: SelectFieldProps<V>) {
  const { field, masks } = useFieldWrapperContext()
  const itemsArray = items as ReadonlyArray<{
    label: React.ReactNode
    value: V
    disabled?: boolean
  }>
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      render={({ id, disabled }) => (
        <Select<V>
          value={field.state.value}
          onValueChange={(value, details) => {
            field.handleChange(value)
            onValueChange?.(value, details)
          }}
          items={items}
          disabled={disabled}
          {...selectProps}
        >
          <SelectTrigger
            id={id}
            onBlur={field.handleBlur}
            aria-invalid={field.state.meta.errors.length > 0}
            className={cn('w-full', className)}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {itemsArray?.map((item) => (
              <SelectItem key={String(item.value)} value={item.value} disabled={item.disabled}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  )
}

// ─── Combobox ────────────────────────────────────────────────────────

interface ComboboxFieldProps<T> extends BaseFieldProps {
  options: T[]
  getKey: (item: T) => string
  getLabel: (item: T) => string
  placeholder?: string
  emptyMessage?: string
}

export function ComboboxField<T>({
  label,
  required,
  description,
  options,
  getKey,
  getLabel,
  placeholder,
  emptyMessage = 'No results found.',
  className,
  layoutClassName,
  layout,
}: ComboboxFieldProps<T>) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      description={description}
      required={required}
      layoutClassName={layoutClassName}
      layout={layout}
      render={({ id, disabled }) => {
        const selected = options.find((opt) => getKey(opt) === field.state.value) ?? null
        return (
          <Combobox<T>
            value={selected}
            onValueChange={(value) => field.handleChange(value ? getKey(value) : null)}
            isItemEqualToValue={(a, b) => getKey(a) === getKey(b)}
            itemToStringLabel={getLabel}
            items={options}
            disabled={disabled}
          >
            <ComboboxInput
              id={id}
              placeholder={placeholder}
              onBlur={field.handleBlur}
              aria-invalid={field.state.meta.errors.length > 0}
              className={cn('w-full', className)}
              showClear
            />
            <ComboboxContent>
              <ComboboxList>
                <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
                <ComboboxCollection>
                  {(opt) => (
                    <ComboboxItem key={getKey(opt)} value={opt}>
                      {getLabel(opt)}
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )
      }}
    />
  )
}

// ─── MultiCombobox ───────────────────────────────────────────────────

interface MultiComboboxFieldProps<T> extends BaseFieldProps {
  options: T[]
  getKey: (item: T) => string
  getLabel: (item: T) => string
  placeholder?: string
  emptyMessage?: string
}

export function MultiComboboxField<T>({
  label,
  required,
  description,
  options,
  getKey,
  getLabel,
  placeholder,
  emptyMessage = 'No results found.',
  className,
  layoutClassName,
  layout,
}: MultiComboboxFieldProps<T>) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      render={({ id, disabled }) => {
        const selected = (field.state.value ?? [])
          .map((key: string) => options.find((opt) => getKey(opt) === key))
          .filter((opt: T | undefined): opt is T => opt !== undefined)
        return (
          <Combobox<T, true>
            multiple
            value={selected}
            onValueChange={(values) => field.handleChange(values.map(getKey))}
            isItemEqualToValue={(a, b) => getKey(a) === getKey(b)}
            itemToStringLabel={getLabel}
            items={options}
            disabled={disabled}
          >
            <ComboboxChips className={cn('w-full', className)}>
              <ComboboxChip />
              <ComboboxChipsInput
                id={id}
                placeholder={selected.length === 0 ? placeholder : undefined}
                onBlur={field.handleBlur}
                aria-invalid={field.state.meta.errors.length > 0}
              />
            </ComboboxChips>
            <ComboboxContent>
              <ComboboxList>
                <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
                <ComboboxCollection>
                  {(opt) => (
                    <ComboboxItem key={getKey(opt)} value={opt}>
                      {getLabel(opt)}
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )
      }}
    />
  )
}

// ─── Checkbox ────────────────────────────────────────────────────────

interface CheckboxFieldProps extends BaseFieldProps {
  disabled?: boolean
}

export function CheckboxField({
  label,
  required,
  description,
  className,
  layoutClassName,
  disabled,
}: CheckboxFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      disabled={disabled}
      layoutClassName={layoutClassName}
      layout="inline"
      reverseLayout
      render={({ id, disabled }) => (
        <Checkbox
          id={id}
          checked={field.state.value ?? false}
          onCheckedChange={(checked) => field.handleChange(checked === true)}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
        />
      )}
    />
  )
}

// ─── CheckboxGroup ───────────────────────────────────────────────────

interface CheckboxGroupFieldProps extends BaseFieldProps {
  options: CheckboxGroupOption[]
  orientation?: 'horizontal' | 'vertical'
}

export function CheckboxGroupField({
  label,
  required,
  description,
  options,
  orientation,
  className,
  layoutClassName,
  layout,
}: CheckboxGroupFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      labelUsesId
      render={({ id, disabled }) => (
        <CheckboxGroup
          options={options}
          value={field.state.value ?? []}
          onValueChange={field.handleChange}
          orientation={orientation}
          disabled={disabled}
          aria-labelledby={label ? id : undefined}
          className={className}
        />
      )}
    />
  )
}

// ─── DatePicker ──────────────────────────────────────────────────────

interface DatePickerFieldProps
  extends BaseFieldProps,
    Omit<DatePickerProps, 'value' | 'onChange'> {}

export function DatePickerField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: DatePickerFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ disabled }) => (
        <DatePicker
          value={field.state.value}
          onChange={field.handleChange}
          disabled={disabled}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── DateInput ───────────────────────────────────────────────────────

interface DateInputFieldProps
  extends BaseFieldProps,
    Omit<DateInputProps, 'value' | 'onChange' | 'onBlur' | 'id' | 'aria-invalid'> {}

export function DateInputField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: DateInputFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <DateInput
          id={id}
          value={field.state.value}
          onChange={field.handleChange}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── DateTimePicker ──────────────────────────────────────────────────

interface DateTimePickerFieldProps
  extends BaseFieldProps,
    Omit<DateTimePickerProps, 'value' | 'onChange'> {}

export function DateTimePickerField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: DateTimePickerFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ disabled }) => (
        <DateTimePicker
          value={field.state.value}
          onChange={field.handleChange}
          disabled={disabled}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── TimePicker ──────────────────────────────────────────────────────

interface TimePickerFieldProps
  extends BaseFieldProps,
    Omit<TimePickerProps, 'value' | 'onChange'> {}

export function TimePickerField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: TimePickerFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ disabled }) => (
        <TimePicker
          value={field.state.value}
          onChange={field.handleChange}
          disabled={disabled}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── RadioGroup ──────────────────────────────────────────────────────

interface RadioGroupFieldItem {
  label: ReactNode
  value: string
  disabled?: boolean
}

interface RadioGroupFieldProps extends BaseFieldProps {
  items: readonly RadioGroupFieldItem[]
  /** "pills" renders toggle-like pill buttons; "radio" renders classic dot-style radios. */
  variant?: 'pills' | 'radio'
  size?: 'default' | 'sm' | 'lg'
}

/** Single-select radio group — always has exactly one value selected. */
export function RadioGroupField({
  label,
  required,
  description,
  items,
  variant = 'pills',
  size,
  className,
  layoutClassName,
  layout,
}: RadioGroupFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  const groupId = useId()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      render={({ disabled }) => (
        <RadioGroup
          value={field.state.value ?? undefined}
          onValueChange={(value) => field.handleChange(value)}
          disabled={disabled}
          variant={variant === 'pills' ? 'pills' : 'default'}
          size={size}
          className={cn('w-full', className)}
        >
          {items.map((item) => {
            const id = `${groupId}-${item.value}`
            if (variant === 'pills') {
              return (
                <RadioGroupItem
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  className="flex-1"
                >
                  {item.label}
                </RadioGroupItem>
              )
            }

            return (
              <div key={item.value} className="flex items-center gap-2">
                <RadioGroupItem id={id} value={item.value} disabled={item.disabled} />
                <label
                  htmlFor={id}
                  className="font-normal peer-data-disabled:cursor-not-allowed peer-data-disabled:opacity-50"
                >
                  {item.label}
                </label>
              </div>
            )
          })}
        </RadioGroup>
      )}
    />
  )
}

// ─── ToggleGroup ─────────────────────────────────────────────────────

interface ToggleGroupFieldItem {
  label: ReactNode
  value: string
  disabled?: boolean
}

interface ToggleGroupFieldProps extends BaseFieldProps {
  items: readonly ToggleGroupFieldItem[]
  variant?: 'default' | 'outline'
  size?: 'default' | 'sm' | 'lg'
}

/** Single-select toggle group — maps the selected value to a string field. */
export function ToggleGroupField({
  label,
  required,
  description,
  items,
  variant = 'outline',
  size,
  className,
  layoutClassName,
  layout,
}: ToggleGroupFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      render={({ disabled }) => (
        <ToggleGroup
          value={field.state.value ? [field.state.value] : []}
          onValueChange={(values) => {
            // Single-select: take the last pressed value (base-ui sends the full array)
            const next = values.length > 0 ? values[values.length - 1] : null
            field.handleChange(next)
          }}
          disabled={disabled}
          variant={variant}
          size={size}
          className={cn('w-full', className)}
        >
          {items.map((item) => (
            <ToggleGroupItem
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className="flex-1"
              aria-label={typeof item.label === 'string' ? item.label : undefined}
            >
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
    />
  )
}
